import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Popover } from "@mui/material"
import { useState } from "react"
import CloseIcon from "@mui/icons-material/Close"
import { Button } from "react-bootstrap"
import { useLocalization } from "../../providers/LocalizationProvider"
import { useSelector } from "react-redux"
import { selectWebsiteData } from "../../redux/general/selectors"
import { renderStyleSheet } from "../../css/styles"

function Modal({
    cancelButton = true,
    disableSave,
    disableSaveBtn,
    children,
    maxWidth,
    modalHeading,
    status,
    onSave,
    onClose,
    deleteButton,
    onDelete,
    deleteTitle,
    confirmBtn,
    isSubmitting = false,
    modalContentStyles,
    className,
    saveBtnLabel,
}) {
    const [deleteConfirmationAnchor, setDeleteConfirmationAnchor] = useState(null)

    const { voiceiiTags } = useLocalization()
    const websiteData = useSelector(selectWebsiteData)

    renderStyleSheet(websiteData)

    const PrimaryColor = websiteData?.primary_colour || "#000000"

    const openDeleteConfirmation = (event) => {
        setDeleteConfirmationAnchor(event.currentTarget)
    }

    const closeDeleteConfirmation = () => {
        setDeleteConfirmationAnchor(null)
    }

    const handleDeleteConfirmed = () => {
        if (onDelete) onDelete()
        closeDeleteConfirmation()
    }

    return (
        <Dialog
            fullWidth
            maxWidth={maxWidth || "md"}
            open={status}
            onClose={onClose}
            className={className}
            BackdropProps={{
                sx: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
            }}
            PaperProps={{
                sx: { borderRadius: "16px" },
            }}
        >
            {modalHeading && (
                <Box>
                    <DialogTitle className="fw-bold" sx={{ color: PrimaryColor }}>
                        {voiceiiTags(modalHeading)}
                    </DialogTitle>
                </Box>
            )}

            <CloseIcon className="e-modal-close-btn" onClick={onClose} />

            {!modalHeading && <Box sx={{ mb: 2 }} />}

            <DialogContent sx={{ ...modalContentStyles }}>{children}</DialogContent>

            <DialogActions className="e-padding-16px">
                {/* Cancel Button */}
                {cancelButton && (
                    <Button
                        variant="custom-varient"
                        onClick={onClose}
                        className="e-modal-cancel-btn"
                    >
                        {voiceiiTags("Cancel")}
                    </Button>
                )}

                {/* Submit Button */}
                {!disableSave && !confirmBtn && (
                    <Button
                        className="d-flex align-items-center justify-content-center e-modal-submit-btn"
                        variant="secondary"
                        onClick={onSave}
                        disabled={disableSaveBtn}
                    >
                        {saveBtnLabel || voiceiiTags("Submit")}
                        {isSubmitting && (
                            <div
                                className="spinner-border spinner-border-sm float-end"
                                role="status"
                            >
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        )}
                    </Button>
                )}

                {/* Delete Button */}
                {deleteButton && (
                    <>
                        <Button
                            variant="danger"
                            onClick={openDeleteConfirmation}
                            disabled={disableSaveBtn}
                        >
                            {voiceiiTags("Delete")}
                        </Button>

                        <Popover
                            open={Boolean(deleteConfirmationAnchor)}
                            anchorEl={deleteConfirmationAnchor}
                            onClose={closeDeleteConfirmation}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            <div className="e-padding-16px">
                                <p>
                                    {deleteTitle
                                        ? deleteTitle
                                        : voiceiiTags("Are you sure you want to delete?")}
                                </p>

                                <div className="e-modal-delete-btn-container">
                                    <Button variant="secondary" onClick={closeDeleteConfirmation}>
                                        {voiceiiTags("Cancel")}
                                    </Button>

                                    {/* ✅ FIXED CONDITION */}
                                    <Button variant="danger" onClick={handleDeleteConfirmed}>
                                        {voiceiiTags("Yes, Delete")}
                                    </Button>
                                </div>
                            </div>
                        </Popover>
                    </>
                )}

                {/* Confirm Button */}
                {confirmBtn && (
                    <Button
                        variant="secondary"
                        className="d-flex align-items-center justify-content-center e-modal-submit-btn"
                        onClick={onSave}
                        disabled={disableSaveBtn}
                    >
                        {isSubmitting ? (
                            <>
                                {voiceiiTags("Confirming")}
                                <div
                                    className="ms-1 spinner-border spinner-border-sm float-end"
                                    role="status"
                                >
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </>
                        ) : (
                            <>{voiceiiTags("Confirm")}</>
                        )}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default Modal
